import { reactive } from "vue";
import { regenerateBrowserId } from "@/telemetry/browserId";
import { sleep } from "@/middleware/helper-functions.js";
import axios from "axios";
import Cookies from "js-cookie";

const state = reactive({
  access_token: Cookies.get("access_token") || "",
  current_user: {} as any,
  isImpersonating: Cookies.get("is_impersonating") === "true",
  originalAccessToken: Cookies.get("impersonator_access_token") || null,
});

async function logout() {
  try {
    Cookies.remove("access_token");
    Cookies.remove("emitente_id");
    Cookies.remove("impersonator_access_token");
    Cookies.remove("is_impersonating");
    Cookies.remove("browser_id");
    await axios.post("/logout");
  } catch (err) {
    console.error("Error during logout:", err);
  }
  location.reload();
}

async function authRequest(payload: any) {
  const isRegister = payload.action === "register";
  const isReset = payload.action === "password-reset";
  const url = isRegister ? "/register" : isReset ? "/password/reset" : "/login";
  const base = { email: payload.email, password: payload.password };
  const body = {
    ...base,
    ...(isRegister && { name: payload.name, password_confirmation: payload.password_confirmation }),
    ...(isReset && { password_confirmation: payload.password_confirmation, token: payload.token }),
  };
  const expires = payload.remember === true ? 90 : 1;
  const { data } = await axios.post(url, body);
  const token = "Bearer " + data.access_token;
  Cookies.set("access_token", token, { expires });
  axios.defaults.headers.common["Authorization"] = state.access_token = token;
  axios.defaults.headers.common["X-Browser-ID"] = regenerateBrowserId();
  await currentUserRequest();
}

async function currentUserRequest() {
  try {
    state.current_user = (await axios.get("/api/user")).data.data;
  } catch (err) {
    console.error("Error in currentUserRequest:", err);
    if (axios.isAxiosError(err) && err.response?.status === 401) {
      await logout();
    }
  }
}

async function ensureIsFetched() {
  while (state.current_user.role_id === undefined) {
    await sleep(100);
  }
}

async function searchImpersonationUsers(query: string) {
  const { data } = await axios.get(`/api/impersonation/search-users?query=${query}`);
  return data;
}

async function startImpersonation(userId: number) {
  try {
    const { data } = await axios.post(`/api/impersonation/start/${userId}`);
    Cookies.set("impersonator_access_token", state.access_token, { expires: 365 });
    Cookies.set("is_impersonating", "true", { expires: 365 });
    Cookies.set("access_token", "Bearer " + data.access_token, { expires: 1 });
    location.reload();
  } catch (err) {
    console.error("Failed to start impersonation:", err);
  }
}

function stopImpersonation() {
  Cookies.set("access_token", state.originalAccessToken!, { expires: 365 });
  Cookies.remove("impersonator_access_token");
  Cookies.remove("is_impersonating");
  location.reload();
}

const getCurrentUser = () => state.current_user;
const getCurrentUserId = (): number => state.current_user.identificador;
const isAuthenticated = () => !!state.access_token;
const isGuest = () => !isAuthenticated();

export default {
  authRequest,
  currentUserRequest,
  ensureIsFetched,
  getCurrentUser,
  getCurrentUserId,
  isAuthenticated,
  isGuest,
  logout,
  searchImpersonationUsers,
  startImpersonation,
  state,
  stopImpersonation,
};
